// Executive Office group card — one operational decision, many steps.
// Renders a proposal group with outcome-first summary, human step states,
// and three actions: Approve Ready Steps / Review Individually / Not Now.
// Individual step technical details live inside <ProposedChangeCard/> which
// is opened one-at-a-time in "Review Individually" mode.
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { friendlyError } from "@/lib/friendlyError";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Sparkles,
  Clock,
  HelpCircle,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { ProposedChangeCard } from "./ProposedChangeCard";
import type { ProposalGroup, StepState } from "@/lib/proposalGroups";

const STATE_META: Record<
  StepState,
  { label: string; icon: React.ReactNode; tone: string }
> = {
  Ready: { label: "Ready", icon: <Check className="h-3.5 w-3.5" />, tone: "text-emerald-700" },
  "Needs Your Choice": {
    label: "Needs Your Choice",
    icon: <HelpCircle className="h-3.5 w-3.5" />,
    tone: "text-amber-700",
  },
  "Waiting on Information": {
    label: "Waiting on Information",
    icon: <Clock className="h-3.5 w-3.5" />,
    tone: "text-muted-foreground",
  },
  "Already Complete": {
    label: "Already Complete",
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    tone: "text-emerald-700/80",
  },
  "Couldn't Finish Yet": {
    label: "Couldn't Finish Yet",
    icon: <AlertCircle className="h-3.5 w-3.5" />,
    tone: "text-destructive",
  },
};

type Mode =
  | { kind: "summary" }
  | { kind: "review"; index: number }
  | { kind: "approving" }
  | { kind: "report"; completed: string[]; remaining: string[] }
  | { kind: "dismissed" };

export function ProposalGroupCard({
  group,
  functionName = "silver-sage-assistant",
  onChanged,
}: {
  group: ProposalGroup;
  functionName?: string;
  onChanged?: () => void;
}) {
  const [mode, setMode] = useState<Mode>({ kind: "summary" });

  const call = async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke(functionName, { body });
    if (error) throw new Error(error.message);
    if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
    return data;
  };

  const approveReady = async () => {
    if (group.readyIds.length === 0) return;
    setMode({ kind: "approving" });
    const completed: string[] = [];
    const remaining: string[] = [];
    for (const step of group.steps) {
      if (step.state !== "Ready") {
        remaining.push(step.outcome);
        continue;
      }
      try {
        await call({ action: "apply", pendingId: step.proposal.pending_id });
        completed.push(step.outcome);
      } catch (e) {
        friendlyError({
          what: `I couldn't finish "${step.outcome}" for ${group.subject}.`,
          nextStep: "The other steps still went through — try this one again in a moment.",
          technical: e,
        });
        remaining.push(step.outcome);
      }
    }
    onChanged?.();
    setMode({ kind: "report", completed, remaining });
  };

  const dismissGroup = async () => {
    // "Not Now" just closes the card locally. The proposals stay pending so
    // the group reappears next time — never marked complete, never deleted.
    setMode({ kind: "dismissed" });
  };

  // Terminal: dismissed
  if (mode.kind === "dismissed") {
    return (
      <QuietBanner
        title="Not now"
        detail={`I'll keep ${group.subject}'s items ready for when you come back.`}
      />
    );
  }

  // Terminal: report
  if (mode.kind === "report") {
    return (
      <div className="rounded-xl border border-emerald-600/30 bg-emerald-50/50 p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
          <CheckCircle2 className="h-4 w-4" />
          {mode.completed.length > 0
            ? `${mode.completed.length} step${mode.completed.length === 1 ? "" : "s"} done for ${group.subject}.`
            : `Nothing completed for ${group.subject} yet.`}
        </div>
        {mode.completed.length > 0 && (
          <ul className="text-sm space-y-1">
            {mode.completed.map((c) => (
              <li key={c} className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-emerald-700 shrink-0" /> {c}
              </li>
            ))}
          </ul>
        )}
        {mode.remaining.length > 0 && (
          <div className="text-sm">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Still to do
            </div>
            <ul className="space-y-1 text-muted-foreground">
              {mode.remaining.map((r) => (
                <li key={r} className="flex items-start gap-2">
                  <Clock className="h-4 w-4 mt-0.5 shrink-0" /> {r}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Individual review mode
  if (mode.kind === "review") {
    const step = group.steps[mode.index];
    return (
      <div className="rounded-xl border border-primary/30 bg-card p-4 space-y-3 shadow-sm">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Reviewing step {mode.index + 1} of {group.steps.length} — {group.title}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => setMode({ kind: "review", index: Math.max(0, mode.index - 1) })}
              disabled={mode.index === 0}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() =>
                setMode({
                  kind: "review",
                  index: Math.min(group.steps.length - 1, mode.index + 1),
                })
              }
              disabled={mode.index === group.steps.length - 1}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <ProposedChangeCard proposal={step.proposal} functionName={functionName} />
        <Button size="sm" variant="ghost" onClick={() => setMode({ kind: "summary" })}>
          Back to group
        </Button>
      </div>
    );
  }

  const approving = mode.kind === "approving";

  // Summary
  return (
    <div
      data-proposal-group={group.key}
      className="rounded-xl border border-primary/30 bg-card p-5 space-y-4 shadow-sm scroll-mt-24"
    >
      {/* Header */}
      <div className="space-y-1.5">
        <Badge variant="secondary" className="gap-1">
          <Sparkles className="h-3 w-3" /> {group.category}
        </Badge>
        <h3 className="text-lg font-serif font-semibold leading-tight">{group.title}</h3>
        <p className="text-sm text-muted-foreground">{group.why}</p>
      </div>

      {/* Counts strip */}
      <div className="flex flex-wrap gap-2 text-xs">
        {group.counts.ready > 0 && (
          <Badge variant="outline" className="text-emerald-700 border-emerald-300">
            {group.counts.ready} ready
          </Badge>
        )}
        {group.counts.needsChoice > 0 && (
          <Badge variant="outline" className="text-amber-700 border-amber-300">
            {group.counts.needsChoice} needs your choice
          </Badge>
        )}
        {group.counts.waiting > 0 && (
          <Badge variant="outline">
            {group.counts.waiting} waiting on information
          </Badge>
        )}
        {group.counts.complete > 0 && (
          <Badge variant="outline">{group.counts.complete} already complete</Badge>
        )}
        {group.counts.blocked > 0 && (
          <Badge variant="outline" className="text-destructive border-destructive/40">
            {group.counts.blocked} couldn't finish yet
          </Badge>
        )}
      </div>

      {/* Included steps */}
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
          Included
        </div>
        <ul className="space-y-1.5">
          {group.steps.map((s, i) => {
            const meta = STATE_META[s.state];
            return (
              <li key={s.proposal.pending_id ?? i} className="flex items-start gap-2 text-sm">
                <span className={`mt-0.5 shrink-0 ${meta.tone}`}>{meta.icon}</span>
                <span className="flex-1">{s.outcome}</span>
                <span className={`text-xs shrink-0 ${meta.tone}`}>{meta.label}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Recommendation */}
      <div className="rounded-md bg-muted/50 border border-border/60 px-3 py-2 text-sm italic">
        {group.recommendation}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {group.readyIds.length > 0 && (
          <Button size="sm" onClick={approveReady} disabled={approving} className="gap-1">
            {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Approve Ready Steps
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => setMode({ kind: "review", index: 0 })}
          disabled={approving}
        >
          Review Individually
        </Button>
        <Button size="sm" variant="ghost" onClick={dismissGroup} disabled={approving}>
          Not Now
        </Button>
      </div>
    </div>
  );
}

function QuietBanner({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
      <div className="font-medium">{title}</div>
      <div className="text-xs text-muted-foreground mt-1">{detail}</div>
    </div>
  );
}
